import { ReactNode } from 'react';

import { ColorToken } from '@backrs_ui/common-ui';

import { Box, SxProps, Theme, Typography } from '@mui/material';

type DetailHeaderProps = Readonly<{
  id: string;
  /** Stacked month/day badge (e.g. from `dayBadge`). Omitted when undefined. */
  badge?: { month: string; day: number };
  /** Custom leading visual (e.g. a thumbnail); takes precedence over `badge`. */
  leading?: ReactNode;
  title: string;
  /** Rendered inline after the title (e.g. a chip). */
  titleAdornment?: ReactNode;
  subtitle?: string;
  /** Arbitrary content rendered under the title instead of `subtitle`. */
  subtitleContent?: ReactNode;
  actions?: ReactNode;
  /**
   * Compact icon actions. When the container is narrower than 600px they sit beside the identity
   * block and `actions` drop below a divider; otherwise both render together on the right.
   */
  secondaryActions?: ReactNode;
  /** Makes the whole card clickable; clicks inside the action slots do not bubble to it. */
  onClick?: () => void;
  /** Card border; defaults to the standard card border. */
  borderColor?: ColorToken;
}>;

export const DetailHeader = ({
  id,
  badge,
  leading,
  title,
  titleAdornment,
  subtitle,
  subtitleContent,
  actions,
  secondaryActions,
  onClick,
  borderColor,
}: DetailHeaderProps) => (
  <Box
    sx={onClick ? clickableRootSx(borderColor) : rootSx(borderColor)}
    id={id}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? (event) => {
            if (
              event.target === event.currentTarget &&
              (event.key === 'Enter' || event.key === ' ')
            ) {
              event.preventDefault();
              onClick();
            }
          }
        : undefined
    }
  >
    <Box sx={identitySx}>
      {leading}
      {!leading && badge && (
        <Box sx={badgeSx} aria-hidden>
          <Box sx={badgeMonthSx}>
            <Typography variant="text-xs-semibold" sx={badgeMonthTextSx}>
              {badge.month}
            </Typography>
          </Box>
          <Box sx={badgeDaySx}>
            <Typography variant="text-lg-bold" sx={badgeDayTextSx}>
              {badge.day}
            </Typography>
          </Box>
        </Box>
      )}
      <Box sx={textColSx}>
        <Box sx={titleRowSx}>
          <Typography variant="text-lg-semibold" sx={titleSx}>
            {title}
          </Typography>
          {titleAdornment}
        </Box>
        {subtitleContent}
        {!subtitleContent && subtitle && (
          <Typography variant="text-sm-regular" sx={subtitleSx}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>

    {secondaryActions && (
      <Box sx={secondaryActionsSx} onClick={(event) => event.stopPropagation()}>
        {secondaryActions}
      </Box>
    )}

    {secondaryActions && actions && <Box sx={dividerSx} aria-hidden />}

    {actions && (
      <Box
        sx={secondaryActions ? stackedActionsSx : actionsSx}
        onClick={(event) => event.stopPropagation()}
      >
        {actions}
      </Box>
    )}
  </Box>
);

const STACKED_QUERY = '@container (max-width: 599px)';
// Phone width: the icon group beside the identity block leaves the title a few dozen
// pixels and it breaks mid-word, so give the identity the whole row and let the icons wrap.
const NARROW_QUERY = '@container (max-width: 449px)';

export const detailHeaderChipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  height: 'auto',
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  boxShadow: backrs.shadows['shadow-xs'],
  '& .MuiChip-label': {
    ...typography['text-xs-medium'],
    color: backrs.colors['Colors/Text/text-secondary'],
    paddingInline: backrs.spacing['spacing-sm'],
    paddingBlock: backrs.spacing['spacing-xxs'],
  },
});

/** Swaps the "Secondary color" (brand) pill to success tones for a completed state. */
export const detailHeaderCompletePillSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-success-primary'],
  borderColor: backrs.colors['Colors/Foreground/fg-success-secondary'],
  backgroundColor: backrs.colors['Colors/Background/bg-success-primary'],
  '&:hover': {
    color: backrs.colors['Colors/Text/text-success-primary'],
    backgroundColor: backrs.colors['Colors/Background/bg-success-secondary'],
  },
});

const rootStyles = ({ backrs }: Theme, borderColor?: ColorToken) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-lg'],
  containerType: 'inline-size',
  padding: backrs.spacing['spacing-2xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors[borderColor ?? 'Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-xl'],
});

const rootSx =
  (borderColor?: ColorToken): SxProps<Theme> =>
  (theme) =>
    rootStyles(theme, borderColor);

const clickableRootSx =
  (borderColor?: ColorToken): SxProps<Theme> =>
  (theme) => ({
    ...rootStyles(theme, borderColor),
    cursor: 'pointer',
    transition: 'box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out',
    '&:hover': {
      boxShadow: theme.backrs.shadows['shadow-lg'],
      transform: 'translate(-2px, -2px)',
    },
  });

const identitySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
  minWidth: 0,
  flex: '1 1 0',
  [NARROW_QUERY]: { flexBasis: '100%' },
});

const badgeSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flexShrink: 0,
  overflow: 'hidden',
  width: '64px',
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const badgeMonthSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  paddingTop: backrs.spacing['spacing-xs'],
  paddingBottom: backrs.spacing['spacing-xxs'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
});

const badgeMonthTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
  lineHeight: 1.5,
});

const badgeDaySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  paddingTop: backrs.spacing['spacing-xxs'],
  paddingBottom: backrs.spacing['spacing-sm'],
});

const badgeDayTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-secondary'],
  lineHeight: 1,
});

const textColSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
  minWidth: 0,
  paddingTop: backrs.spacing['spacing-sm'],
});

const titleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-md'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  wordBreak: 'break-word',
});

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const actionsStyles = ({ backrs }: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-lg'],
  order: 2,
});

const actionsSx: SxProps<Theme> = actionsStyles;

/** Used when `secondaryActions` share the card, so these actions drop below the divider. */
const stackedActionsSx: SxProps<Theme> = (theme) => ({
  ...actionsStyles(theme),
  [STACKED_QUERY]: { width: '100%' },
  // Once the icons sit on their own row the card reads better with one left edge.
  [NARROW_QUERY]: { justifyContent: 'flex-start' },
});

const secondaryActionsSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  order: 3,
  [STACKED_QUERY]: { order: 1 },
};

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'none',
  order: 1,
  [STACKED_QUERY]: {
    display: 'block',
    width: `calc(100% + 2 * ${backrs.spacing['spacing-2xl']})`,
    marginInline: `calc(-1 * ${backrs.spacing['spacing-2xl']})`,
    borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  },
});
